import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  getCustomWallpapers,
  getWallpaperFit,
  getWallpaperId,
  WALLPAPER_EVENT,
  WALLPAPER_FIT_EVENT,
  WALLPAPER_POSITION_EVENT,
  applyRemoteWallpaperSettings,
} from "@/lib/wallpaper";

/**
 * Syncs wallpaper prefs between devices via the /user/settings endpoint.
 *
 * On sign-in it fetches the server copy and applies it to this device (so a
 * fresh Android install picks up the same wallpaper you set on web). On any
 * subsequent change it debounces + PUTs the whole wallpaper block back to
 * the server, keeping every device up to date.
 *
 * Failures are silent — localStorage is the fallback source of truth, and
 * the next successful save will overwrite the server anyway.
 */
export default function WallpaperSync() {
  const { user } = useAuth();
  const hydratedFor = useRef(null);
  const pushTimer = useRef(null);
  const suppressPush = useRef(false);

  // Hydrate on user change (login OR page refresh with an existing token).
  useEffect(() => {
    if (!user?.user_id) return;
    if (hydratedFor.current === user.user_id) return;
    hydratedFor.current = user.user_id;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/user/settings");
        if (cancelled || !r.data) return;
        const s = r.data;
        if (s.custom_wallpapers || s.wallpaper_id !== undefined || s.wallpaper_fit) {
          suppressPush.current = true;
          applyRemoteWallpaperSettings({
            wallpaper_id: s.wallpaper_id,
            wallpaper_fit: s.wallpaper_fit,
            custom_wallpapers: s.custom_wallpapers,
          });
          // Give the event listeners one tick to run before re-enabling push,
          // otherwise the hydrate itself would trigger a PUT.
          setTimeout(() => { suppressPush.current = false; }, 300);
        }
      } catch { /* offline — localStorage remains the source of truth */ }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id]);

  // Debounced push on every wallpaper change.
  useEffect(() => {
    if (!user?.user_id) return;
    const push = () => {
      if (suppressPush.current) return;
      if (pushTimer.current) clearTimeout(pushTimer.current);
      pushTimer.current = setTimeout(async () => {
        try {
          await api.put("/user/settings", {
            wallpaper_id: getWallpaperId(),
            wallpaper_fit: getWallpaperFit(),
            custom_wallpapers: getCustomWallpapers(),
          });
        } catch { /* silent — will retry on next change */ }
      }, 700);
    };
    window.addEventListener(WALLPAPER_EVENT, push);
    window.addEventListener(WALLPAPER_FIT_EVENT, push);
    window.addEventListener(WALLPAPER_POSITION_EVENT, push);
    return () => {
      window.removeEventListener(WALLPAPER_EVENT, push);
      window.removeEventListener(WALLPAPER_FIT_EVENT, push);
      window.removeEventListener(WALLPAPER_POSITION_EVENT, push);
      if (pushTimer.current) clearTimeout(pushTimer.current);
    };
  }, [user?.user_id]);

  return null;
}
